import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  DocumentDuplicateIcon,
  BoltIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

/**
 * Executive dashboard — super-admin-only view of KeyScan usage per account.
 *
 * Designed to answer at a glance: which accounts are actually using the product,
 * how heavily, and how well it's working for them (retention as a quality signal).
 * Modeled on the Cottfile "Executive View" dashboard pattern.
 */

type Range = 1 | 7 | 30 | 90 | 365;

interface AccountRow {
  account_id: number;
  slug: string;
  name: string;
  users: number;
  documents_processed: number;
  invoices_retained: number;
  retention_pct: number | null;
  api_calls: number;
  spend_usd: number;
  cost_per_doc_usd: number | null;
  // All-time last activity (any API call ever). Used in the tooltip when a
  // row is dormant in the current window.
  last_seen: string | null;
  // Last activity scoped to the picked date range. When null and the row is
  // dormant/never, the row shows "No activity in period" instead of a stale
  // relative time.
  last_seen_in_window: string | null;
  // Lifecycle pill next to the account name. Computed server-side against
  // the current window per the 2026-08-27 research on B2B tenant dashboards.
  lifecycle_state: 'active' | 'dormant' | 'never';
}

interface SummaryResponse {
  date_range: { start: string; end: string; days: number };
  totals: {
    documents_processed: number;
    invoices_retained: number;
    retention_pct: number | null;
    api_calls: number;
    spend_usd: number;
    cost_per_doc_usd: number | null;
    active_accounts: number;
    total_accounts: number;
  };
  by_provider: Record<
    string,
    { calls: number; units: number; input_tokens: number; output_tokens: number; cost_usd: number }
  >;
  accounts: AccountRow[];
}

interface ActivityItem {
  invoice_id: number;
  filename: string | null;
  vendor: string | null;
  invoice_number: string | null;
  invoice_amount: number | null;
  created_at: string | null;
  account_id: number | null;
  account_name: string | null;
  account_slug: string | null;
  username: string | null;
}

interface ActivityResponse {
  items: ActivityItem[];
}

const RANGE_LABELS: { value: Range; label: string }[] = [
  { value: 1, label: 'Today' },
  { value: 7, label: 'Weekly' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 365, label: 'YTD' },
];

const fmtUsd = (n: number | null | undefined, digits = 2) =>
  n == null ? '$0.00' : `$${n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

const fmtInt = (n: number | null | undefined) => (n == null ? '0' : n.toLocaleString());

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return (
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  );
};

const fmtLastSeen = (iso: string | null) => {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  valueClass?: string;
}> = ({ label, value, sub, Icon, valueClass }) => (
  <div className="flex-1 min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </span>
      <Icon className="h-5 w-5 text-gray-400" />
    </div>
    <div className={`text-3xl font-semibold ${valueClass || 'text-gray-900 dark:text-white'}`}>{value}</div>
    {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>}
  </div>
);

const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [range, setRange] = useState<Range>(30);
  // Custom yyyy-mm-dd overrides. When either is set, they trump `range`.
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [activity, setActivity] = useState<ActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  // Per 2026-08-25 meeting: surface failing Keystone syncs prominently at
  // the top of the admin dashboard so we're not blind to broken tenants
  // between nightly sync runs.
  const [failedSyncs, setFailedSyncs] = useState<Array<{
    account_id: number;
    slug: string;
    name: string;
    vendor_status: string | null;
    gl_status: string | null;
    vendor_error: string | null;
    gl_error: string | null;
  }>>([]);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<number | 'all'>('all');
  const [hideInactive, setHideInactive] = useState(false);

  const customActive = !!(customStart || customEnd);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const activityUrl =
        selectedAccountId === 'all'
          ? `/admin/dashboard/recent-activity?limit=25`
          : `/admin/dashboard/recent-activity?limit=25&account_id=${selectedAccountId}`;
      const summaryParams = new URLSearchParams({ days: String(range) });
      if (customStart) summaryParams.set('start', customStart);
      if (customEnd) summaryParams.set('end', customEnd);
      const [s, a, ks] = await Promise.all([
        api.get<SummaryResponse>(`/admin/dashboard/summary?${summaryParams.toString()}`),
        api.get<ActivityResponse>(activityUrl),
        api.get<Array<{
          account_id: number;
          slug: string;
          name: string;
          vendor: { last_status: string | null; last_error: string | null } | null;
          gl: { last_status: string | null; last_error: string | null } | null;
        }>>(`/admin/keystone/status`).catch(() => ({ data: [] })),
      ]);
      setSummary(s.data);
      setActivity(a.data);
      setLastRefresh(new Date());
      // Collect tenants where either vendor or GL sync last errored
      const failed = (ks.data || [])
        .filter((r) => r.vendor?.last_status === 'error' || r.gl?.last_status === 'error')
        .map((r) => ({
          account_id: r.account_id,
          slug: r.slug,
          name: r.name,
          vendor_status: r.vendor?.last_status ?? null,
          gl_status: r.gl?.last_status ?? null,
          vendor_error: r.vendor?.last_error ?? null,
          gl_error: r.gl?.last_error ?? null,
        }));
      setFailedSyncs(failed);
    } catch (e: any) {
      const msg =
        e?.response?.status === 403
          ? 'You do not have super-admin access to this dashboard.'
          : e?.response?.data?.detail || e?.message || 'Failed to load dashboard.';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [range, customStart, customEnd, selectedAccountId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const t = setInterval(fetchAll, 60_000);
    return () => clearInterval(t);
  }, [fetchAll]);

  // Hooks below MUST run in the same order on every render, so the
  // super-admin redirect happens AFTER the derived-value hooks. React's
  // Rules of Hooks otherwise flag this as a hard error at build time.
  const filteredAccounts = useMemo(() => {
    if (!summary?.accounts) return [];
    const term = search.trim().toLowerCase();
    return summary.accounts.filter((a) => {
      if (selectedAccountId !== 'all' && a.account_id !== selectedAccountId) return false;
      if (hideInactive && a.lifecycle_state !== 'active') return false;
      if (term && !a.name.toLowerCase().includes(term) && !a.slug.toLowerCase().includes(term)) {
        return false;
      }
      return true;
    });
  }, [summary, search, selectedAccountId, hideInactive]);

  const providerRows = useMemo(() => {
    if (!summary?.by_provider) return [];
    return Object.entries(summary.by_provider).map(([provider, v]) => ({ provider, ...v }));
  }, [summary]);

  if (!user?.is_super_admin) return <Navigate to="/" replace />;

  const activeInPeriod = summary?.totals.active_accounts ?? 0;
  const totalTenants = summary?.totals.total_accounts ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-brand-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">K</span>
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                KeyScan <span className="text-gray-400 font-normal uppercase text-xs tracking-wider">Executive View</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {activeInPeriod} of {totalTenants} accounts active this period
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>Updated {lastRefresh ? fmtLastSeen(lastRefresh.toISOString()) : '…'}</span>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-green-600 dark:text-green-400 font-medium">LIVE</span>
            </div>
            <button
              onClick={fetchAll}
              disabled={loading}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Refresh now"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Sync-failure alert banner. Surface broken nightly syncs at
            the top so operators notice
            immediately when a tenant's data is going stale. */}
        {failedSyncs.length > 0 && (
          <div className="mb-4 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/60 text-red-700 dark:text-red-300 text-xs font-bold">
                  !
                </span>
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-red-800 dark:text-red-200">
                  {failedSyncs.length} tenant{failedSyncs.length === 1 ? '' : 's'} with failing Keystone sync
                </div>
                <div className="text-xs text-red-700 dark:text-red-300 mt-1">
                  Users on these tenants may see stale or missing vendor / GL data. Open each tenant's Keystone Sync modal to see the exact error and retry.
                </div>
                <div className="mt-2 space-y-1">
                  {failedSyncs.map((f) => {
                    const parts: string[] = [];
                    if (f.vendor_status === 'error') parts.push('vendor');
                    if (f.gl_status === 'error') parts.push('GL');
                    return (
                      <div key={f.account_id} className="text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">{f.name}</span>
                        <span className="text-gray-500 dark:text-gray-400"> ({f.slug})</span>
                        <span className="text-red-700 dark:text-red-300">: {parts.join(' + ')} failing</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Range picker */}
        <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {customActive ? (
              <>
                Showing custom range
                <strong className="text-gray-700 dark:text-gray-300">
                  {' '}
                  {customStart || 'start'} → {customEnd || 'today'}
                </strong>.
              </>
            ) : (
              <>
                Showing data from the last{' '}
                <strong className="text-gray-700 dark:text-gray-300">
                  {range === 365 ? 'year' : `${range} day${range === 1 ? '' : 's'}`}
                </strong>.
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
              {RANGE_LABELS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => {
                    setRange(r.value);
                    setCustomStart('');
                    setCustomEnd('');
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    !customActive && range === r.value
                      ? 'bg-brand-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1">
              <input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="text-xs bg-transparent text-gray-700 dark:text-gray-200 focus:outline-none"
                aria-label="Custom start date"
              />
              <span className="text-xs text-gray-400">→</span>
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="text-xs bg-transparent text-gray-700 dark:text-gray-200 focus:outline-none"
                aria-label="Custom end date"
              />
              {customActive && (
                <button
                  onClick={() => {
                    setCustomStart('');
                    setCustomEnd('');
                  }}
                  className="ml-1 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Clear custom range"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/*
          KPI cards. The old labels swapped documents and invoices, which confused
          everyone in the meeting. Correcting:
          - Documents = each PDF uploaded (count of processing jobs)
          - Pages = each OCR-scanned page (Reducto's units, how we get billed)
          - Invoices = individual invoice records extracted (count of Invoice)
          - Cost = external API cost (Reducto + OpenAI)
        */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <KpiCard
            label="Documents"
            value={fmtInt(summary?.totals.documents_processed)}
            sub={`each PDF uploaded`}
            Icon={DocumentTextIcon}
          />
          <KpiCard
            label="Pages"
            value={fmtInt(summary?.by_provider?.reducto?.units)}
            sub={`OCR pages scanned (Reducto billing unit)`}
            Icon={DocumentDuplicateIcon}
          />
          <KpiCard
            label="Invoices"
            value={fmtInt(summary?.totals.invoices_retained)}
            sub={`individual invoice records extracted`}
            Icon={DocumentDuplicateIcon}
          />
          <KpiCard
            label="API Calls"
            value={fmtInt(summary?.totals.api_calls)}
            sub={`Reducto + OpenAI combined`}
            Icon={BoltIcon}
          />
          <KpiCard
            label="Cost"
            value={fmtUsd(summary?.totals.spend_usd)}
            sub={
              summary?.totals.cost_per_doc_usd != null
                ? `~${fmtUsd(summary.totals.cost_per_doc_usd, 3)} per document`
                : `Reducto + OpenAI combined`
            }
            Icon={BanknotesIcon}
          />
          <KpiCard
            label="Revenue"
            value={fmtUsd(0)}
            sub={`billing not yet enabled (placeholder)`}
            Icon={CurrencyDollarIcon}
          />
        </div>

        {/* Provider breakdown */}
        {providerRows.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-6">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Spend by provider
            </div>
            <div className="flex flex-wrap gap-6">
              {providerRows.map((p) => (
                <div key={p.provider} className="flex-1 min-w-[160px]">
                  <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">{p.provider}</div>
                  <div className="text-xl font-semibold text-gray-900 dark:text-white mt-0.5">
                    {fmtUsd(p.cost_usd)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {fmtInt(p.calls)} calls · {fmtInt(p.units)} units
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search accounts by name or slug…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div className="relative">
            <select
              value={selectedAccountId}
              onChange={(e) =>
                setSelectedAccountId(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">All accounts</option>
              {(summary?.accounts ?? []).map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="h-4 w-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
            />
            Hide inactive
          </label>
          <div className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
            {filteredAccounts.length} of {summary?.accounts?.length ?? 0} accounts shown
          </div>
        </div>

        {/* Per-account table.
            Single scope caption above the table (per 2026-08-27 dashboard-UX
            research) so every column can stay generically labeled while the
            reader still knows what window the numbers cover. */}
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Showing metrics for <span className="font-medium text-gray-700 dark:text-gray-300">{(RANGE_LABELS.find((r) => r.value === range) || RANGE_LABELS[2]).label}</span>. Lifecycle pill and last-activity column reflect the same window.
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Account</th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Users</th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Documents</th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Invoices</th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">API Calls</th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cost</th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cost / doc</th>
                  <th className="text-right px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last activity (in period)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredAccounts.map((a) => {
                  const rangeLabel = (RANGE_LABELS.find((r) => r.value === range) || RANGE_LABELS[2]).label;
                  const pill =
                    a.lifecycle_state === 'active'
                      ? { text: 'Active', cls: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300' }
                      : a.lifecycle_state === 'dormant'
                        ? { text: `Idle in ${rangeLabel}`, cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200' }
                        : { text: 'New tenant', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' };
                  const pillTitle =
                    a.lifecycle_state === 'active'
                      ? `Had documents, invoices, or API calls in ${rangeLabel}.`
                      : a.lifecycle_state === 'dormant'
                        ? `No activity in ${rangeLabel}. All-time last seen: ${a.last_seen ? fmtDate(a.last_seen) : 'unknown'}. This tenant has used the product before.`
                        : 'This tenant has never uploaded a document or made an API call.';
                  return (
                  <tr key={a.account_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-900 dark:text-white">{a.name}</div>
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${pill.cls}`}
                          title={pillTitle}
                        >
                          {pill.text}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{a.slug}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{fmtInt(a.users)}</td>
                    {/* Per 2026-08-27 meeting: Documents = each PDF uploaded
                        (= documents_processed), Invoices = extracted records
                        (= invoices_retained). Previous swap was wrong. */}
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">{fmtInt(a.documents_processed)}</td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{fmtInt(a.invoices_retained)}</td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{fmtInt(a.api_calls)}</td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{fmtUsd(a.spend_usd)}</td>
                    <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">
                      {a.cost_per_doc_usd != null ? fmtUsd(a.cost_per_doc_usd, 3) : 'n/a'}
                    </td>
                    <td className="px-5 py-3 text-right text-xs text-gray-500 dark:text-gray-400">
                      {a.last_seen_in_window ? (
                        fmtLastSeen(a.last_seen_in_window)
                      ) : (
                        <span
                          className="italic text-gray-400 dark:text-gray-500"
                          title={
                            a.last_seen
                              ? `No activity in ${rangeLabel}. All-time last seen: ${fmtDate(a.last_seen)}.`
                              : 'This tenant has never uploaded a document or made an API call.'
                          }
                        >
                          None in {rangeLabel}
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {!loading && filteredAccounts.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No accounts match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent activity */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                Recent activity
                {selectedAccountId !== 'all' && (
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                    (filtered to {summary?.accounts.find((a) => a.account_id === selectedAccountId)?.name ?? 'account'})
                  </span>
                )}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                latest {activity?.items?.length ?? 0} invoices
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Invoice</th>
                  <th className="text-left px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Vendor</th>
                  <th className="text-left px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Account</th>
                  <th className="text-left px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">User</th>
                  <th className="text-right px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Invoice amount</th>
                  <th className="text-right px-5 py-2.5 font-medium text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Processed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(activity?.items ?? []).map((i) => (
                  <tr key={i.invoice_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-5 py-3">
                      <div className="font-mono text-xs text-gray-600 dark:text-gray-400">
                        {i.invoice_number || `#${i.invoice_id}`}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 truncate max-w-[220px]">
                        {i.filename || '—'}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-700 dark:text-gray-300">{i.vendor || '—'}</td>
                    <td className="px-5 py-3">
                      <div className="text-gray-700 dark:text-gray-300">{i.account_name || '—'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{i.account_slug || ''}</div>
                    </td>
                    <td className="px-5 py-3 text-gray-700 dark:text-gray-300">{i.username || '—'}</td>
                    <td className="px-5 py-3 text-right text-gray-700 dark:text-gray-300">
                      {i.invoice_amount != null ? fmtUsd(i.invoice_amount) : '—'}
                    </td>
                    <td className="px-5 py-3 text-right text-xs text-gray-500 dark:text-gray-400">
                      {fmtDate(i.created_at)}
                    </td>
                  </tr>
                ))}
                {!loading && (activity?.items?.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No recent invoices for this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboardPage;
